/* eslint-disable react/no-unescaped-entities */
// v3.15-1069 — Transactional SMS modal.
//
// SAFETY PRINCIPLES (mirror the backend safety chain in routes/sms.py):
//   • SMS sending is OFF BY DEFAULT (ENABLE_TRANSACTIONAL_SMS=false). The
//     modal still RENDERS so operators can see the form, but the Confirm
//     button is gated by the config response.
//   • Initial deployment is in TEST MODE — a big amber banner makes that
//     unambiguous, and the Confirm button copy reads "Run TEST simulation".
//   • LIVE MODE shows a red banner + a one-time admin acknowledgement
//     checkbox panel. Confirm is disabled until the admin POSTs to
//     /api/admin/sms-config/acknowledge with the exact consent text.
//   • Email-first gate must be answered Yes before the Send panel appears.
//   • Reason selector required. "Other" requires a non-empty textarea.
//
// Under no circumstance should this modal be capable of producing a live
// send without the backend's seven gates ALSO passing.
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, MessageSquare, Send, Loader2, Mail, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import ClientLocalTime from './ClientLocalTime';

const REASON_OPTIONS = [
  { value: 'client_cannot_find_email',        label: 'Client cannot find email' },
  { value: 'client_cannot_access_spam_folder', label: 'Client cannot access spam folder' },
  { value: 'email_not_received',              label: 'Email not received' },
  { value: 'urgent_completion',               label: 'Urgent completion needed' },
  { value: 'other',                           label: 'Other (please specify)' },
];

const REQUIRED_ACK_TEXT = 'I understand this will send a real SMS message to a real client.';

const formatPhone = (p) => {
  if (!p) return '';
  const digits = p.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (p.startsWith('+')) return p;
  return digits ? `+${digits}` : '';
};

const TransactionalSmsModal = ({ open, onOpenChange, client, smsType }) => {
  // smsType: 'credit_app' or 'ach'
  const titleByType = {
    credit_app: 'Send Credit App Link by SMS',
    ach:        'Send ACH Authorization Link by SMS',
  };

  // Config state from /api/admin/sms-config
  const [cfg, setCfg] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);

  // Step 1: email-first answer
  const [emailFirstAttempted, setEmailFirstAttempted] = useState(null); // null/true/false

  // Step 2: send form
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [phone, setPhone] = useState(formatPhone(client?.phone || ''));
  const [sending, setSending] = useState(false);

  // Step 3 (LIVE-only): admin acknowledgement
  const [ackChecked, setAckChecked] = useState(false);
  const [ackTextInput, setAckTextInput] = useState('');
  const [ackSubmitting, setAckSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset form on close
      setEmailFirstAttempted(null);
      setReasonCode('');
      setReasonText('');
      setPhone(formatPhone(client?.phone || ''));
      setAckChecked(false);
      setAckTextInput('');
      return;
    }
    setPhone(formatPhone(client?.phone || ''));
    setLoadingCfg(true);
    api.get('/admin/sms-config')
      .then((r) => setCfg(r.data))
      .catch((e) => {
        toast.error('Could not load SMS configuration.');
        setCfg(null);
      })
      .finally(() => setLoadingCfg(false));
  }, [open, client?.phone]);

  const isLive = (cfg?.sms_mode === 'live');
  const isEnabled = !!cfg?.enable_transactional_sms;
  const needsAck = isLive && !cfg?.sms_live_acknowledged;

  const handleAcknowledge = async () => {
    setAckSubmitting(true);
    try {
      await api.post('/admin/sms-config/acknowledge', {
        acknowledged: ackChecked,
        confirmation_text: ackTextInput,
      });
      toast.success('Acknowledgement recorded. An admin must now flip SMS_LIVE_ACKNOWLEDGED=true in the .env to activate live mode.');
      // Re-load config so the warning panel clears for this session
      const r = await api.get('/admin/sms-config');
      setCfg(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not record acknowledgement.';
      toast.error(typeof msg === 'string' ? msg : 'Acknowledgement failed.');
    } finally {
      setAckSubmitting(false);
    }
  };

  const canSubmit =
    isEnabled &&
    emailFirstAttempted === true &&
    !!reasonCode &&
    (reasonCode !== 'other' || (reasonText || '').trim().length > 0) &&
    /^\+[1-9]\d{6,14}$/.test(phone) &&
    !(needsAck);

  const handleSend = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const endpoint =
        smsType === 'credit_app'
          ? `/clients/${client.id}/send-credit-app-link-sms`
          : `/clients/${client.id}/send-ach-link-sms`;
      const resp = await api.post(endpoint, {
        phone,
        reason_code: reasonCode,
        reason_text: reasonCode === 'other' ? reasonText : null,
        email_first_attempted: true,
      });
      const out = resp.data;
      if (out?.outcome === 'test_simulated') {
        toast.success(`TEST simulation OK — no real SMS sent. (audit_id: ${out.audit_id?.slice(0, 8)}…)`);
      } else if (out?.outcome === 'sent') {
        toast.success(`Text message queued.${out.provider_message_id ? ' Provider id: ' + out.provider_message_id : ''}`);
      } else {
        toast.warning(`Outcome: ${out?.outcome || 'unknown'}`);
      }
      onOpenChange?.(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const errCode = typeof detail === 'object' ? detail?.error : null;
      const msg = typeof detail === 'object' ? detail?.message : (typeof detail === 'string' ? detail : 'Send failed');
      toast.error(`${errCode ? '['+errCode+'] ' : ''}${msg}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="transactional-sms-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            {titleByType[smsType]}
          </DialogTitle>
        </DialogHeader>

        {/* v3.18-1291 — client's local time is shown BEFORE any send
            gate is presented, so operators immediately see whether
            they are approaching quiet hours in the client's timezone. */}
        {client?.id && (
          <div className="mb-3">
            <ClientLocalTime clientId={client.id} variant="banner" />
          </div>
        )}

        {/* Mode banner — top of modal, unambiguous */}
        {loadingCfg ? (
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500">
            <Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Loading SMS config…
          </div>
        ) : !isEnabled ? (
          <div className="rounded-md bg-slate-100 border border-slate-300 p-3 text-xs text-slate-700 flex items-start gap-2" data-testid="sms-banner-disabled">
            <ShieldAlert className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-slate-800">SMS DISABLED</div>
              <div>Transactional SMS is turned off. An admin must set <code className="bg-slate-200 px-1 rounded">ENABLE_TRANSACTIONAL_SMS=true</code> in the backend .env to activate this feature.</div>
            </div>
          </div>
        ) : isLive ? (
          <div className="rounded-md bg-red-50 border border-red-300 p-3 text-xs text-red-900 flex items-start gap-2" data-testid="sms-banner-live">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-bold text-red-900 tracking-wide uppercase text-sm">LIVE SMS ACTIVE</div>
              <div>This will transmit a real SMS to the recipient. Every send is audited.</div>
            </div>
          </div>
        ) : (
          <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid="sms-banner-test">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-bold text-amber-900 tracking-wide uppercase text-sm">TEST MODE ACTIVE</div>
              <div>No outbound SMS will be sent. The system will simulate the send and write a full audit row.</div>
            </div>
          </div>
        )}

        {/* Live-mode acknowledgement panel (one-time) */}
        {needsAck && (
          <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 space-y-2" data-testid="sms-live-ack-panel">
            <div className="text-xs font-semibold text-red-900">Live mode requires admin acknowledgement.</div>
            <label className="flex items-start gap-2 text-xs text-red-900 cursor-pointer">
              <input
                type="checkbox"
                checked={ackChecked}
                onChange={(e) => setAckChecked(e.target.checked)}
                className="mt-0.5"
                data-testid="sms-live-ack-checkbox"
              />
              <span>I understand this will send a real SMS message to a real client.</span>
            </label>
            <div>
              <Label className="text-[10px] text-red-900">Type the consent text exactly to confirm:</Label>
              <Input
                value={ackTextInput}
                onChange={(e) => setAckTextInput(e.target.value)}
                placeholder={REQUIRED_ACK_TEXT}
                className="h-8 text-xs mt-1"
                data-testid="sms-live-ack-text"
              />
            </div>
            <Button
              size="sm"
              onClick={handleAcknowledge}
              disabled={!ackChecked || ackTextInput.trim() !== REQUIRED_ACK_TEXT || ackSubmitting}
              data-testid="sms-live-ack-submit"
              className="bg-red-600 hover:bg-red-700"
            >
              {ackSubmitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Record Acknowledgement
            </Button>
            <div className="text-[10px] text-red-700">
              Recording this acknowledgement is not enough on its own. A backend admin must
              ALSO set <code className="bg-red-100 px-1 rounded">SMS_LIVE_ACKNOWLEDGED=true</code> in the .env to actually
              enable live transmission.
            </div>
          </div>
        )}

        {/* Email-first gate */}
        {isEnabled && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-semibold text-slate-800 mb-2 flex items-center gap-1">
                <Mail className="h-3 w-3" /> Have you already attempted to send this by email?
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={emailFirstAttempted === true ? 'default' : 'outline'}
                  onClick={() => setEmailFirstAttempted(true)}
                  data-testid="sms-email-first-yes"
                >
                  Yes — continue
                </Button>
                <Button
                  size="sm"
                  variant={emailFirstAttempted === false ? 'default' : 'outline'}
                  onClick={() => {
                    setEmailFirstAttempted(false);
                    toast.info('Please try email delivery first. SMS is for cases where email failed.');
                  }}
                  data-testid="sms-email-first-no"
                >
                  No — I'll try email first
                </Button>
              </div>
              {emailFirstAttempted === false && (
                <div className="mt-2 text-[11px] text-slate-600">
                  SMS is intended for cases where email cannot reach the client. Close this dialog
                  and use the email send action on this page first.
                </div>
              )}
            </div>

            {/* Send form (only after Yes) */}
            {emailFirstAttempted === true && (
              <div className="space-y-3" data-testid="sms-send-form">
                <div>
                  <Label className="text-xs text-slate-600">Why is SMS needed?</Label>
                  <div className="space-y-1 mt-1">
                    {REASON_OPTIONS.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name="sms-reason"
                          value={opt.value}
                          checked={reasonCode === opt.value}
                          onChange={() => setReasonCode(opt.value)}
                          data-testid={`sms-reason-${opt.value}`}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {reasonCode === 'other' && (
                    <Textarea
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value.slice(0, 200))}
                      placeholder="Briefly describe (200 char max)…"
                      className="mt-2 text-xs"
                      rows={2}
                      data-testid="sms-reason-other-text"
                    />
                  )}
                </div>

                <div>
                  <Label className="text-xs text-slate-600">Recipient phone (E.164, e.g. +15551234567)</Label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onBlur={(e) => setPhone(formatPhone(e.target.value))}
                    placeholder="+15551234567"
                    className="h-9 text-sm mt-1"
                    data-testid="sms-phone-input"
                  />
                  {phone && !/^\+[1-9]\d{6,14}$/.test(phone) && (
                    <div className="text-[10px] text-red-600 mt-0.5">Must be a valid E.164 number.</div>
                  )}
                </div>

                {cfg?.sms_allowed_numbers?.length > 0 && (
                  <div className="text-[10px] text-slate-500">
                    Rollout allowlist active. Only these numbers can receive: {cfg.sms_allowed_numbers.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange?.(false)}
            disabled={sending}
            data-testid="sms-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!canSubmit || sending}
            data-testid="sms-confirm-send-btn"
            className={isLive ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
            {isLive ? 'Send LIVE SMS' : 'Run TEST simulation'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionalSmsModal;
